import React from 'react';
import { createPortal } from 'react-dom';
import {
  MapLibreMap,
  Marker,
  GeolocateControl,
  Popup,
  setWorkerUrl,
  type GeoJSONSource,
  type MapGeoJSONFeature,
  type MapMouseEvent,
  type ExpressionSpecification,
  type Point,
  type PositionAnchor,
} from 'maplibre-gl';
import type {
  Feature,
  FeatureCollection,
  Geometry,
  LineString,
  Position,
} from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs';

import type { MapHit } from '../../api/place';
import { isPointInRing } from '../../geo';

// maplibre-gl resolves its worker relative to import.meta.url by default, which points
// at the bundle's own location once webpack bundles this module — not at the real,
// unbundled worker file. Pointing it at the emitted asset URL directly avoids that.
setWorkerUrl(maplibreWorkerUrl);

const HIGHLIGHT_SOURCE_ID = 'highlight';
const HIGHLIGHT_COLOR = '#e11d48';
const EMPTY_COLLECTION: FeatureCollection = { type: 'FeatureCollection', features: [] };

// Слои домов из стиля (public/styles/light.json). Дом под курсором
// перекрашивается через feature-state: в наших тайлах у каждого дома свой
// OSM id (planetiler собирает их без склейки зданий на z14 - см. README).
// На z13 тайлы склеивают соседние дома в кварталы, поэтому там не подсвечиваем.
const BUILDING_LAYER_IDS = ['building', 'building-3d'];
const BUILDING_2D_LAYER_ID = 'building';
const BUILDING_3D_LAYER_ID = 'building-3d';
const BUILDING_HOVER_MIN_ZOOM = 14;

// Цвет дома по назначению - атрибут class в тайлах (жилой, школа, ...; его
// добавляет наш planetiler, см. README), [обычный, под курсором]. Под
// курсором - на полтона темнее (каналы * 0.9): дома рисуются с прозрачностью
// 0.8 поверх фона, так на экране выходит ~8% темнее. Без class (в OSM просто
// building=yes, и на z13, где атрибутов нет) - прежний цвет домов из стиля.
interface BuildingPalette {
  readonly byClass: Record<string, readonly [string, string]>;
  readonly fallback: readonly [string, string];
}
const BUILDING_COLORS: BuildingPalette = {
  byClass: {
    residential: ['#DFCFB9', '#C9BAA6'],
    other: ['#D3D0CB', '#BEBBB7'], // нежилое
    school: ['#E6D59E', '#CFC08E'],
    kindergarten: ['#EECDAB', '#D6B89A'],
    healthcare: ['#E9C3C3', '#D2B0B0'],
  },
  fallback: ['#D9D1C3', '#C3BCB0'],
};
// тёмная тема (холодная гамма, как у фона): дома чуть светлее фона, под
// курсором - ещё светлее; назначение - лёгким оттенком
const BUILDING_COLORS_DARK: BuildingPalette = {
  byClass: {
    residential: ['#2C3B47', '#394B59'],
    other: ['#283540', '#34444F'],
    school: ['#363F38', '#434E45'],
    kindergarten: ['#3A3A3E', '#47474D'],
    healthcare: ['#3A3440', '#47404F'],
  },
  fallback: ['#2A3843', '#364653'],
};

// match собирается из палитры, а тип кортежа match у MapLibre требует хотя
// бы одной пары литералом - отсюда приведение через unknown
const buildingColor = (palette: BuildingPalette, hovered: boolean): ExpressionSpecification =>
  [
    'match',
    ['get', 'class'],
    ...Object.entries(palette.byClass).flatMap(([kind, colors]) => [kind, colors[hovered ? 1 : 0]]),
    palette.fallback[hovered ? 1 : 0],
  ] as unknown as ExpressionSpecification;

/**
 * Узоры тёмного стиля, которых нет в спрайте (make-dark-style.mjs меняет
 * fill-pattern на "<имя>_dark"): рисуются здесь, когда стиль их запросит.
 * pedestrian_polygon - штриховка пешеходных площадей, повторяет узор
 * спрайта (диагональ x-y с шагом 4px, прозрачность 241/73/0/73), но
 * полосами на полтона светлее тёмного фона.
 */
const DARK_PATTERNS: Record<string, () => ImageData> = {
  pedestrian_polygon_dark: () => {
    const size = 64;
    const alpha = [241, 73, 0, 73];
    const data = new Uint8ClampedArray(size * size * 4);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const i = (y * size + x) * 4;
        data.set([62, 80, 92, alpha[(((x - y) % 4) + 4) % 4]], i);
      }
    }
    return new ImageData(data, size, size);
  },
};

// Подъезды выбранного дома (не все подряд - иначе в плотной застройке ими
// усыпано всё): точки из слоя entrance тайлов, отобранные по контуру дома,
// и стрелки снаружи у стены, указывающие на вход.
const ENTRANCE_SOURCE_ID = 'selected-entrances';
const ENTRANCE_LAYER_ID = 'selected-entrance-circle';
const ENTRANCE_ARROW_IMAGE = 'entrance-arrow';
// подъезд "принадлежит" дому, если стоит на его стене (не дальше, чем)
const ENTRANCE_MAX_WALL_DISTANCE_M = 3;
// стрелка - на таком расстоянии от стены, снаружи
const ENTRANCE_ARROW_OFFSET_M = 6;
const METERS_PER_DEGREE = 111_320;

/** Стрелка вверх (поворачивается на карте к входу), белая с тёмной обводкой. */
function drawEntranceArrow(): ImageData {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.beginPath();
  ctx.moveTo(16, 3);
  ctx.lineTo(27, 16);
  ctx.lineTo(20, 16);
  ctx.lineTo(20, 29);
  ctx.lineTo(12, 29);
  ctx.lineTo(12, 16);
  ctx.lineTo(5, 16);
  ctx.closePath();
  ctx.fillStyle = '#1a73e8';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

const ROUTE_SOURCE_ID = 'route';
const ROUTE_COLOR = '#1a73e8';
export const ROUTE_TOLL_COLOR = '#f57c00';
const ROUTE_START_COLOR = '#1e7e34';
const ROUTE_END_COLOR = '#e11d48';
const ROUTE_VIA_COLOR = '#1a73e8';
const ROUTE_ALTERNATIVE_COLOR = '#9aa9bd';

// клик "попал" в дорогу, если её линия ближе ROAD_HIT_PX; подпись улицы
// ищем среди линий transportation_name не дальше STREET_NAME_PX - они
// лежат в тайлах отдельно от линий дорог и не всегда совпадают с ними
const ROAD_HIT_PX = 5;
const STREET_NAME_PX = 24;
// стандартный маркер стоит остриём в точке и занимает ~38px над ней: сверху
// подсказка открывается над ним, сбоку - напротив его середины. Сторону
// MapLibre выбирает сам, чтобы подсказка не уходила за край карты.
const MARKER_HEIGHT_PX = 38;
const MARKER_HALF_WIDTH_PX = 14;
const POPUP_OFFSET: Record<PositionAnchor, [number, number]> = {
  center: [0, -MARKER_HEIGHT_PX / 2],
  top: [0, 0],
  'top-left': [0, 0],
  'top-right': [0, 0],
  bottom: [0, -MARKER_HEIGHT_PX],
  'bottom-left': [0, -MARKER_HEIGHT_PX],
  'bottom-right': [0, -MARKER_HEIGHT_PX],
  left: [MARKER_HALF_WIDTH_PX, -MARKER_HEIGHT_PX / 2],
  right: [-MARKER_HALF_WIDTH_PX, -MARKER_HEIGHT_PX / 2],
};

export interface MapViewHandle {
  flyTo: (lat: number, lon: number, zoom?: number) => void;
  /** Перелёт к точке, только если её не видно или карта мельче minZoom. */
  showPoint: (lat: number, lon: number, minZoom: number) => void;
  setMarker: (lat: number, lon: number) => void;
  clearMarker: () => void;
  /** Подсвечивает контур объекта (полигон - обводка + лёгкая заливка, линия -
   *  просто обводка); null убирает подсветку. */
  setHighlight: (geometry: Geometry | null) => void;
  /** bbox: [minLon, minLat, maxLon, maxLat]; padding - отступы от краёв
   *  карты в px (по умолчанию 60 со всех сторон), например чтобы объект не
   *  уходил под панель поверх карты. */
  fitBounds: (
    bbox: readonly [number, number, number, number],
    maxZoom?: number,
    padding?: { top: number; right: number; bottom: number; left: number },
  ) => void;
  /** Линия маршрута, её платные отрезки (рисуются поверх другим цветом) и
   *  невыбранные варианты (серые, под ней); null убирает маршрут. */
  setRoute: (line: LineString | null, tolls?: readonly LineString[], alternatives?: readonly LineString[]) => void;
  /** Маркеры точек маршрута по порядку ([lat, lon]; null - точка не задана):
   *  первая - зелёная, последняя - красная, промежуточные - синие. */
  setRoutePoints: (points: readonly ([number, number] | null)[]) => void;
  /** Дом в точке [lat, lon] выбран (клик, поиск) - показать его подъезды и
   *  стрелки ко входам; null - скрыть. */
  selectBuilding: (point: [number, number] | null) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** Показать, где я (запрос геолокации). */
  locate: () => void;
}

export interface MapViewProps {
  /** Стиль карты; смена (тёмная тема) перезагружает стиль на лету - маршрут,
   *  подсветка и маркеры сохраняются. */
  readonly styleUrl: string;
  /** Тёмная тема - палитра домов (она задаётся здесь, а не в стиле). */
  readonly dark?: boolean;
  readonly initialView: {
    readonly lat: number;
    readonly lng: number;
    readonly zoom: number;
    readonly bearing?: number;
    readonly pitch?: number;
  };
  /** hit - что под курсором: здание, улица или ничего из этого. */
  readonly onMapClick?: (lat: number, lon: number, hit: MapHit) => void;
  /** Fired after the map stops moving (pan/zoom/flyTo), with the new center
   *  and the visible area [minLon, minLat, maxLon, maxLat] - used to bias
   *  address search results towards what's currently on screen. */
  readonly onMoveEnd?: (
    lat: number,
    lon: number,
    bbox: [number, number, number, number],
    view: { zoom: number; bearing: number; pitch: number },
  ) => void;
  /** Всплывающая подсказка над точкой [lat, lon]; null - закрыта. */
  readonly popup?: { readonly lat: number; readonly lon: number; readonly content: React.ReactNode } | null;
  /** Подсказку закрыли крестиком (но не сменой popup на null). */
  readonly onPopupClose?: () => void;
  /** Режим выбора точки на карте - курсор-прицел вместо обычного. */
  readonly pickMode?: boolean;
}

const MapView = React.forwardRef<MapViewHandle, MapViewProps>(function MapView(
  { styleUrl, initialView, onMapClick, onMoveEnd, popup, onPopupClose, pickMode = false, dark = false },
  ref,
) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<MapLibreMap | null>(null);
  const markerRef = React.useRef<Marker | null>(null);
  // подсветка, запрошенная до загрузки стиля - применяется в обработчике 'load'
  const highlightRef = React.useRef<Geometry | null>(null);
  // то же для линии маршрута
  const routeRef = React.useRef<RouteLines>({ line: null, tolls: [], alternatives: [] });
  const geolocateRef = React.useRef<GeolocateControl | null>(null);
  const selectedBuildingRef = React.useRef<[number, number] | null>(null);
  // подъезды выбранного дома - переживают смену стиля (тёмная тема)
  const entrancesRef = React.useRef<FeatureCollection>(EMPTY_COLLECTION);
  const routePointMarkersRef = React.useRef<{ marker: Marker; color: string }[]>([]);
  const onMapClickRef = React.useRef(onMapClick);
  onMapClickRef.current = onMapClick;
  const onMoveEndRef = React.useRef(onMoveEnd);
  onMoveEndRef.current = onMoveEnd;
  const onPopupCloseRef = React.useRef(onPopupClose);
  onPopupCloseRef.current = onPopupClose;
  const darkRef = React.useRef(dark);
  darkRef.current = dark;
  const pickModeRef = React.useRef(pickMode);
  pickModeRef.current = pickMode;
  const popupRef = React.useRef<Popup | null>(null);
  // remove() из кода тоже шлёт 'close' - его не выдаём за закрытие крестиком
  const isRemovingPopupRef = React.useRef(false);
  const [popupContainer] = React.useState(() => document.createElement('div'));

  React.useEffect(() => {
    if (!containerRef.current) return;

    const map = new MapLibreMap({
      container: containerRef.current,
      style: styleUrl,
      center: [initialView.lng, initialView.lat],
      zoom: initialView.zoom,
      bearing: initialView.bearing ?? 0,
      pitch: initialView.pitch ?? 0,
    });

    // кнопки карты - свои (MapControls на ui-kit); у GeolocateControl берём
    // только поведение (точка "я здесь", слежение), его кнопка скрыта стилями
    const geolocate = new GeolocateControl({ positionOptions: { enableHighAccuracy: true } });
    map.addControl(geolocate, 'bottom-right');
    geolocateRef.current = geolocate;

    const handleClick = (event: MapMouseEvent) => {
      const entrance = entranceAt(map, event.point);
      if (entrance) {
        // дом под подъездом - для адреса (сам подъезд стоит на его стене)
        const house = buildingAt(map, event)?.point ?? null;
        onMapClickRef.current?.(entrance.lat, entrance.lon, {
          kind: 'entrance',
          ref: entrance.ref,
          flats: entrance.flats,
          house: house ? [house[1], house[0]] : null,
        });
        return;
      }
      const building = buildingAt(map, event);
      const streetName = building ? null : streetNameAt(map, event.point);
      const hit: MapHit = building
        ? { kind: 'building' }
        : streetName
          ? { kind: 'street', name: streetName }
          : { kind: 'other' };
      // у стены или крыши точка на земле под курсором лежит за домом -
      // адрес ищем по точке внутри самого дома
      const [lon, lat] = building?.point ?? [event.lngLat.lng, event.lngLat.lat];
      onMapClickRef.current?.(lat, lon, hit);
    };
    map.on('click', handleClick);

    const handleMoveEnd = () => {
      const center = map.getCenter();
      const bounds = map.getBounds();
      onMoveEndRef.current?.(
        center.lat,
        center.lng,
        [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
        { zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch() },
      );
    };
    map.on('moveend', handleMoveEnd);

    const handleImageMissing = ({ id }: { id: string }) => {
      const draw = DARK_PATTERNS[id];
      if (draw && !map.hasImage(id)) map.addImage(id, draw());
    };
    map.on('styleimagemissing', handleImageMissing);

    // style.load - и при первой загрузке, и после setStyle (смена темы): наши
    // источники и слои живут в стиле и после смены его пропадают
    map.on('style.load', () => {
      // узоры тёмного стиля - сразу, до тайлов: добавленный по
      // styleimagemissing узор тайлы, разобранные параллельно, не получали
      for (const [id, draw] of Object.entries(DARK_PATTERNS)) {
        if (!map.hasImage(id)) map.addImage(id, draw());
      }
      if (!map.hasImage(ENTRANCE_ARROW_IMAGE)) map.addImage(ENTRANCE_ARROW_IMAGE, drawEntranceArrow());
      map.addSource(HIGHLIGHT_SOURCE_ID, { type: 'geojson', data: EMPTY_COLLECTION });
      // под подписями, чтобы заливка не мешала их читать
      const styleLayers = map.getStyle().layers;
      const firstSymbolLayerId = styleLayers.find(layer => layer.type === 'symbol')?.id;
      // маршрут и подсветка улиц - над всеми линиями дорог: первый symbol-слой
      // (стрелки одностороннего движения) идёт в стиле раньше мостов, и мосты
      // рисовались поверх линии маршрута
      const lastRoadLineIndex = styleLayers.findLastIndex(
        layer =>
          layer.type === 'line' &&
          'source-layer' in layer &&
          layer['source-layer'] === 'transportation',
      );
      const aboveRoadsLayerId =
        lastRoadLineIndex >= 0 ? styleLayers[lastRoadLineIndex + 1]?.id : firstSymbolLayerId;
      map.addLayer(
        {
          id: 'highlight-fill',
          type: 'fill',
          source: HIGHLIGHT_SOURCE_ID,
          // fill-слой не отбрасывает линии, а замыкает каждую в многоугольник
          // и заливает: у улицы (MultiLineString из десятков кусков) между
          // кусками и их хордами появлялись розовые "клинья". Заливаем
          // только полигоны (регион/район/город), линии - только обводкой.
          filter: ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false],
          paint: { 'fill-color': HIGHLIGHT_COLOR, 'fill-opacity': 0.08 },
        },
        aboveRoadsLayerId,
      );
      map.addLayer(
        {
          id: 'highlight-line',
          type: 'line',
          source: HIGHLIGHT_SOURCE_ID,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': HIGHLIGHT_COLOR, 'line-width': 1.5, 'line-opacity': 0.9 },
        },
        aboveRoadsLayerId,
      );
      if (highlightRef.current) applyHighlight(map, highlightRef.current);

      // маршрут - белая подложка + цветная линия поверх, под подписями;
      // платные отрезки - отдельные объекты того же источника (kind=toll),
      // рисуются поверх основной линии своим цветом
      map.addSource(ROUTE_SOURCE_ID, { type: 'geojson', data: EMPTY_COLLECTION });
      // невыбранные варианты маршрута - серые, под основным
      map.addLayer(
        {
          id: 'route-alternative',
          type: 'line',
          source: ROUTE_SOURCE_ID,
          filter: ['==', ['get', 'kind'], 'alternative'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ROUTE_ALTERNATIVE_COLOR,
            'line-width': ['interpolate', ['linear'], ['zoom'], 4, 3, 10, 5, 16, 8],
            'line-opacity': 0.8,
          },
        },
        aboveRoadsLayerId,
      );
      map.addLayer(
        {
          id: 'route-casing',
          type: 'line',
          source: ROUTE_SOURCE_ID,
          filter: ['==', ['get', 'kind'], 'main'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#ffffff',
            'line-width': ['interpolate', ['linear'], ['zoom'], 4, 5, 10, 8, 16, 12],
          },
        },
        aboveRoadsLayerId,
      );
      map.addLayer(
        {
          id: 'route-line',
          type: 'line',
          source: ROUTE_SOURCE_ID,
          filter: ['==', ['get', 'kind'], 'main'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ROUTE_COLOR,
            'line-width': ['interpolate', ['linear'], ['zoom'], 4, 3, 10, 5, 16, 8],
          },
        },
        aboveRoadsLayerId,
      );
      map.addLayer(
        {
          id: 'route-toll',
          type: 'line',
          source: ROUTE_SOURCE_ID,
          filter: ['==', ['get', 'kind'], 'toll'],
          // butt - чтобы отрезок не "наползал" скруглением на бесплатную часть
          layout: { 'line-join': 'round', 'line-cap': 'butt' },
          paint: {
            'line-color': ROUTE_TOLL_COLOR,
            'line-width': ['interpolate', ['linear'], ['zoom'], 4, 3, 10, 5, 16, 8],
          },
        },
        aboveRoadsLayerId,
      );
      applyRoute(map, routeRef.current);

      const palette = darkRef.current ? BUILDING_COLORS_DARK : BUILDING_COLORS;
      if (map.getLayer(BUILDING_2D_LAYER_ID)) {
        map.setPaintProperty(BUILDING_2D_LAYER_ID, 'fill-color', buildingColor(palette, false));
      }
      if (map.getLayer(BUILDING_3D_LAYER_ID)) {
        map.setPaintProperty(BUILDING_3D_LAYER_ID, 'fill-extrusion-color', [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          buildingColor(palette, true),
          buildingColor(palette, false),
        ]);
      }

      // подъезды выбранного дома - над домами и подписями
      map.addSource(ENTRANCE_SOURCE_ID, { type: 'geojson', data: EMPTY_COLLECTION });
      map.addLayer({
        id: 'selected-entrance-arrow',
        type: 'symbol',
        source: ENTRANCE_SOURCE_ID,
        filter: ['==', ['get', 'kind'], 'arrow'],
        layout: {
          'icon-image': ENTRANCE_ARROW_IMAGE,
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-size': ['interpolate', ['linear'], ['zoom'], 15, 0.45, 18, 0.8, 20, 1],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      map.addLayer({
        id: ENTRANCE_LAYER_ID,
        type: 'circle',
        source: ENTRANCE_SOURCE_ID,
        filter: ['==', ['get', 'kind'], 'entrance'],
        paint: {
          'circle-color': '#1a73e8',
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, ['case', ['has', 'ref'], 5, 3], 19, ['case', ['has', 'ref'], 10, 5]],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
        },
      });
      map.addLayer({
        id: 'selected-entrance-label',
        type: 'symbol',
        source: ENTRANCE_SOURCE_ID,
        filter: ['all', ['==', ['get', 'kind'], 'entrance'], ['has', 'ref']],
        layout: {
          'text-field': ['get', 'ref'],
          'text-font': ['Noto Sans Bold'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 15, 8, 19, 13],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: { 'text-color': '#ffffff' },
      });
      applyEntrances(map, entrancesRef.current);
    });

    let hovered: { key: string; source: string; sourceLayer: string; ids: (string | number)[] } | null = null;
    const setBuildingHover = (building: ReturnType<typeof buildingAt>) => {
      map.getCanvas().style.cursor = pickModeRef.current ? 'crosshair' : building ? 'pointer' : '';
      const canHighlight = building?.sourceLayer && map.getZoom() >= BUILDING_HOVER_MIN_ZOOM;
      const key = canHighlight ? building.key : null;
      if (key === (hovered?.key ?? null)) return;

      if (hovered) {
        const { source, sourceLayer } = hovered;
        for (const id of hovered.ids) map.removeFeatureState({ source, sourceLayer, id }, 'hover');
      }
      hovered = canHighlight
        ? { key: building.key, source: building.source, sourceLayer: building.sourceLayer!, ids: building.ids() }
        : null;
      if (hovered) {
        const { source, sourceLayer } = hovered;
        for (const id of hovered.ids) map.setFeatureState({ source, sourceLayer, id }, { hover: true });
      }
    };
    const handleMouseMove = (event: MapMouseEvent) => {
      setBuildingHover(buildingAt(map, event));
      if (!pickModeRef.current && entranceAt(map, event.point)) map.getCanvas().style.cursor = 'pointer';
    };
    const handleMouseOut = () => setBuildingHover(null);
    map.on('mousemove', handleMouseMove);
    map.on('mouseout', handleMouseOut);
    // Report the initial center too, so a search made before the first pan/zoom
    // is still biased towards it instead of having no viewport context at all.
    handleMoveEnd();

    mapRef.current = map;

    return () => {
      map.off('click', handleClick);
      map.off('styleimagemissing', handleImageMissing);
      map.off('mousemove', handleMouseMove);
      map.off('mouseout', handleMouseOut);
      map.off('moveend', handleMoveEnd);
      popupRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // styleUrl/initialView are only read once, on mount; changing them later is done
    // imperatively (flyTo, setStyle in the effect below) rather than by
    // re-creating the map instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const map = mapRef.current;
    if (map) map.getCanvas().style.cursor = pickMode ? 'crosshair' : '';
  }, [pickMode]);

  // смена темы: новый стиль, наши слои добавит обработчик style.load
  const loadedStyleRef = React.useRef(styleUrl);
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || loadedStyleRef.current === styleUrl) return;
    loadedStyleRef.current = styleUrl;
    map.setStyle(styleUrl, { diff: false });
  }, [styleUrl]);

  const popupLat = popup?.lat;
  const popupLon = popup?.lon;
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (popupLat == null || popupLon == null) {
      isRemovingPopupRef.current = true;
      popupRef.current?.remove();
      isRemovingPopupRef.current = false;
      return;
    }
    if (!popupRef.current) {
      popupRef.current = new Popup({
        offset: POPUP_OFFSET,
        closeOnClick: false,
        maxWidth: '320px',
      }).setDOMContent(popupContainer);
      popupRef.current.on('close', () => {
        if (!isRemovingPopupRef.current) onPopupCloseRef.current?.();
      });
    }
    popupRef.current.setLngLat([popupLon, popupLat]);
    // addTo у открытой подсказки сначала закрывает её (с событием 'close')
    if (!popupRef.current.isOpen()) popupRef.current.addTo(map);
  }, [popupLat, popupLon, popupContainer]);

  React.useImperativeHandle(
    ref,
    () => ({
      flyTo: (lat, lon, zoom) => {
        const map = mapRef.current;
        if (!map) return;
        map.flyTo({ center: [lon, lat], zoom: zoom ?? map.getZoom(), essential: true });
      },
      showPoint: (lat, lon, minZoom) => {
        const map = mapRef.current;
        if (!map) return;
        const zoom = map.getZoom();
        if (zoom >= minZoom && map.getBounds().contains([lon, lat])) return;
        map.flyTo({ center: [lon, lat], zoom: Math.max(zoom, minZoom), essential: true });
      },
      setMarker: (lat, lon) => {
        const map = mapRef.current;
        if (!map) return;
        if (!markerRef.current) {
          markerRef.current = new Marker({ color: '#e11d48' });
        }
        markerRef.current.setLngLat([lon, lat]).addTo(map);
      },
      clearMarker: () => {
        markerRef.current?.remove();
      },
      setHighlight: geometry => {
        highlightRef.current = geometry;
        const map = mapRef.current;
        if (map) applyHighlight(map, geometry);
      },
      fitBounds: (bbox, maxZoom, padding) => {
        const map = mapRef.current;
        if (!map) return;
        map.fitBounds(
          [
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]],
          ],
          { padding: padding ?? 60, maxZoom: maxZoom ?? 16, essential: true },
        );
      },
      setRoute: (line, tolls = [], alternatives = []) => {
        routeRef.current = { line, tolls: line ? tolls : [], alternatives: line ? alternatives : [] };
        const map = mapRef.current;
        if (map) applyRoute(map, routeRef.current);
      },
      selectBuilding: point => {
        selectedBuildingRef.current = point;
        const map = mapRef.current;
        if (!map) return;
        if (!point) {
          entrancesRef.current = EMPTY_COLLECTION;
          applyEntrances(map, EMPTY_COLLECTION);
          return;
        }
        // после перелёта к дому его тайлы ещё грузятся - ищем, когда карта
        // успокоится; если за это время выбрали другой дом, этот уже не нужен
        const run = () => {
          if (selectedBuildingRef.current !== point) return;
          entrancesRef.current = buildingEntrances(map, point);
          applyEntrances(map, entrancesRef.current);
        };
        if (map.loaded() && !map.isMoving()) run();
        map.once('idle', run);
      },
      zoomIn: () => mapRef.current?.zoomIn(),
      zoomOut: () => mapRef.current?.zoomOut(),
      locate: () => {
        geolocateRef.current?.trigger();
      },
      setRoutePoints: points => {
        const map = mapRef.current;
        if (!map) return;
        // цвет маркера MapLibre не меняет - при другой роли точки маркер новый
        const markers = routePointMarkersRef.current;
        markers.forEach(({ marker }) => marker.remove());
        routePointMarkersRef.current = points.flatMap((point, i) => {
          if (!point) return [];
          const color = i === 0 ? ROUTE_START_COLOR : i === points.length - 1 ? ROUTE_END_COLOR : ROUTE_VIA_COLOR;
          const reused = markers.find(m => m.color === color && !m.marker.getElement().isConnected);
          const marker = reused?.marker ?? new Marker({ color, scale: color === ROUTE_VIA_COLOR ? 0.8 : 1 });
          if (reused) markers.splice(markers.indexOf(reused), 1);
          marker.setLngLat([point[1], point[0]]).addTo(map);
          return [{ marker, color }];
        });
      },
    }),
    [],
  );

  return (
    <>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      {popup && createPortal(popup.content, popupContainer)}
    </>
  );
});

/** Здание под курсором - со стенами и крышей, а не только по контуру на
 *  земле. key - для сравнения; ids - id самого здания и его частей,
 *  собираются лениво: при движении мыши внутри того же здания они не нужны. */
function buildingAt(map: MapLibreMap, event: MapMouseEvent) {
  const layers = BUILDING_LAYER_IDS.filter(id => map.getLayer(id));
  const features = layers.length ? map.queryRenderedFeatures(event.point, { layers }) : [];
  const point: Position = [event.lngLat.lng, event.lngLat.lat];

  // Объёмные дома MapLibre ищет по их стенам и крыше на экране и отдаёт
  // ближайшим к камере первым - берём его. Точка на земле под курсором тут
  // не годится: у стены или крыши она лежит за домом. Плоские дома (z13) -
  // по контуру: там склеенные кварталы, нужен полигон, в который попала точка.
  const hit =
    features.find(feature => feature.layer.type === 'fill-extrusion') ??
    features.find(feature => polygonsOf(feature.geometry).some(rings => isPointInPolygon(point, rings)));
  if (!hit) return null;

  const { source, sourceLayer } = hit;
  // курсор мог попасть в часть здания (башенку на крыше ГУМа) - подсвечиваем
  // всё здание, то есть самый крупный объект, внутри которого эта часть
  const id = sourceLayer && !isMergedBuildings(hit) ? outlineOf(map, hit, source, sourceLayer) : hit.id;
  return {
    key: `${source}:${sourceLayer}:${id}`,
    source,
    sourceLayer,
    /** Центр здания - по нему ищется адрес. Не точка клика: у стены или
     *  крыши она лежит за домом, а у длинного дома в плотной застройке
     *  ближайшей к ней бывает точка соседнего дома. */
    point:
      (sourceLayer && id != null && !isMergedBuildings(hit) && buildingCenter(map, source, sourceLayer, id)) ||
      (polygonsOf(hit.geometry).length ? pointInside(polygonsOf(hit.geometry)[0]) : point),
    ids: () =>
      id == null || !sourceLayer || isMergedBuildings(hit) ? [] : buildingWithParts(map, source, sourceLayer, id),
  };
}

/** Центр здания по его контуру во всех загруженных тайлах (тайлы режут дом
 *  по своим границам): центр площади кусков, если он внутри дома, иначе точка
 *  внутри самого большого куска. null - здания нет в загруженных тайлах. */
function buildingCenter(
  map: MapLibreMap,
  source: string,
  sourceLayer: string,
  id: string | number,
): Position | null {
  const pieces = map
    .querySourceFeatures(source, { sourceLayer, filter: ['==', ['id'], id] })
    .flatMap(feature => polygonsOf(feature.geometry));
  if (!pieces.length) return null;

  let area = 0;
  let x = 0;
  let y = 0;
  for (const [outer] of pieces) {
    const [cx, cy, a] = ringCentroid(outer);
    area += a;
    x += cx * a;
    y += cy * a;
  }
  const center: Position = [x / area, y / area];
  if (area > 0 && pieces.some(rings => isPointInPolygon(center, rings))) return center;
  const largest = pieces.reduce((a, b) => (ringArea(b[0]) > ringArea(a[0]) ? b : a));
  return pointInside(largest);
}

/** Центр площади кольца и его площадь (в градусах - только для взвешивания). */
function ringCentroid(ring: Position[]): [number, number, number] {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const cross = ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
    a += cross;
    cx += (ring[j][0] + ring[i][0]) * cross;
    cy += (ring[j][1] + ring[i][1]) * cross;
  }
  if (a === 0) return [ring[0][0], ring[0][1], 0];
  return [cx / (3 * a), cy / (3 * a), Math.abs(a) / 2];
}

/** Точка внутри полигона: центр вершин, если он внутри (так у большинства
 *  домов), иначе середина самого длинного внутреннего отрезка на
 *  горизонтали через этот центр (Г- и П-образные дома). */
function pointInside(rings: Rings): Position {
  const outer = rings[0];
  const n = outer.length - 1 || 1;
  const center: Position = [
    outer.slice(0, n).reduce((sum, p) => sum + p[0], 0) / n,
    outer.slice(0, n).reduce((sum, p) => sum + p[1], 0) / n,
  ];
  if (isPointInPolygon(center, rings)) return center;

  const y = center[1];
  const xs: number[] = [];
  for (const ring of rings) {
    for (let i = 1; i < ring.length; i++) {
      const [x1, y1] = ring[i - 1];
      const [x2, y2] = ring[i];
      if (y1 > y !== y2 > y) xs.push(x1 + ((y - y1) * (x2 - x1)) / (y2 - y1));
    }
  }
  xs.sort((a, b) => a - b);
  let best: Position = outer[0];
  let bestWidth = -1;
  for (let i = 0; i + 1 < xs.length; i += 2) {
    if (xs[i + 1] - xs[i] > bestWidth) {
      bestWidth = xs[i + 1] - xs[i];
      best = [(xs[i] + xs[i + 1]) / 2, y];
    }
  }
  return best;
}

/** id самого крупного здания, внутри которого почти целиком лежит part (или
 *  самой part, если такого нет). */
function outlineOf(
  map: MapLibreMap,
  part: MapGeoJSONFeature,
  source: string,
  sourceLayer: string,
): string | number | undefined {
  const partPolygons = polygonsOf(part.geometry);
  if (!partPolygons.length) return part.id;
  const partBounds = ringBounds(partPolygons[0][0]);
  let best = { id: part.id, area: ringArea(partPolygons[0][0]) };
  for (const feature of map.querySourceFeatures(source, { sourceLayer })) {
    if (feature.id == null || feature.id === part.id || isMergedBuildings(feature)) continue;
    for (const rings of polygonsOf(feature.geometry)) {
      const area = ringArea(rings[0]);
      if (area <= best.area || !boundsIntersect(partBounds, ringBounds(rings[0]))) continue;
      if (partPolygons.every(partRings => isMostlyInside(partRings, rings))) best = { id: feature.id, area };
    }
  }
  return best.id;
}

/**
 * id здания и его частей (building:part) - они лежат в тайлах отдельными
 * объектами со своими высотами: у ГУМа общий контур высотой 19 м, а части
 * крыши поднимаются до 33 м. Части - объекты, почти целиком лежащие внутри
 * контура здания; тайлы режут дома по своим границам, поэтому контур берём
 * из всех тайлов (у кусков одного дома id общий).
 */
function buildingWithParts(
  map: MapLibreMap,
  source: string,
  sourceLayer: string,
  id: string | number,
): (string | number)[] {
  const all = map.querySourceFeatures(source, { sourceLayer });
  const outline = all.filter(feature => feature.id === id).flatMap(feature => polygonsOf(feature.geometry));
  const outlineBounds = outline.map(rings => ringBounds(rings[0]));
  const ids = new Set<string | number>([id]);
  for (const feature of all) {
    if (feature.id == null || ids.has(feature.id) || isMergedBuildings(feature)) continue;
    const isPart = polygonsOf(feature.geometry).some(rings => {
      const bounds = ringBounds(rings[0]);
      return outline.some((piece, i) => boundsIntersect(bounds, outlineBounds[i]) && isMostlyInside(rings, piece));
    });
    if (isPart) ids.add(feature.id);
  }
  return [...ids];
}

type Rings = Position[][];

/**
 * Объект из старых тайлов, где planetiler склеивал соседние дома в один
 * MultiPolygon: feature-state перекрасил бы их все. Тайлы пересобраны
 * 2026-10-21, но браузеры держат старые в кэше до недели (max-age=604800,
 * сбросить его параметром в адресе тайлов нельзя - сервер отвечает 404).
 * В новых тайлах дом - один полигон (двор - дыра в нём), так что после
 * 2026-10-29 проверку можно убрать.
 */
const isMergedBuildings = ({ geometry }: { geometry: Geometry }): boolean => polygonsOf(geometry).length > 1;

const polygonsOf = (geometry: Geometry): Rings[] =>
  geometry.type === 'Polygon'
    ? [geometry.coordinates]
    : geometry.type === 'MultiPolygon'
      ? geometry.coordinates
      : [];

// ~0.5 м: точки ближе к краю части не проверяем - соседние дома с общей
// стеной после квантования в тайле могут на столько заходить друг на друга
const PART_EDGE_EPSILON_DEG = 5e-6;
const PART_GRID = 6;
const PART_INSIDE_SHARE = 0.9;

/** Почти вся площадь inner лежит внутри outer: точки сетки по габаритам
 *  inner, попавшие в inner, почти все попадают и в outer. Соседний дом с
 *  общей стеной сюда не проходит - общих точек у них нет. */
function isMostlyInside(inner: Rings, outer: Rings): boolean {
  const [minX, minY, maxX, maxY] = ringBounds(inner[0]);
  // у мелких частей (слуховое окно 2x3 м) отступ в полметра от края съел бы
  // все точки сетки - берём не больше десятой доли размера
  const epsilon = Math.min(PART_EDGE_EPSILON_DEG, 0.1 * Math.min(maxX - minX, maxY - minY));
  let inInner = 0;
  let inBoth = 0;
  for (let i = 0; i < PART_GRID; i++) {
    for (let j = 0; j < PART_GRID; j++) {
      const point: Position = [
        minX + ((i + 0.5) / PART_GRID) * (maxX - minX),
        minY + ((j + 0.5) / PART_GRID) * (maxY - minY),
      ];
      if (!isPointInPolygon(point, inner) || distanceToRing(point, inner[0]) <= epsilon) {
        continue;
      }
      inInner++;
      if (isPointInPolygon(point, outer)) inBoth++;
    }
  }
  return inInner > 0 && inBoth >= PART_INSIDE_SHARE * inInner;
}

/** Внутри внешнего контура и не в дыре (дворе-колодце). */
const isPointInPolygon = (point: Position, [outer, ...holes]: Rings): boolean =>
  isPointInRing(point, outer) && !holes.some(hole => isPointInRing(point, hole));

/** Площадь в квадратных градусах - только чтобы сравнивать контуры между собой. */
function ringArea(ring: Position[]): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    sum += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  }
  return Math.abs(sum) / 2;
}

const boundsIntersect = (
  [aMinX, aMinY, aMaxX, aMaxY]: [number, number, number, number],
  [bMinX, bMinY, bMaxX, bMaxY]: [number, number, number, number],
): boolean => aMinX <= bMaxX && bMinX <= aMaxX && aMinY <= bMaxY && bMinY <= aMaxY;

function ringBounds(ring: Position[]): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return [minX, minY, maxX, maxY];
}

function distanceToRing([x, y]: Position, ring: Position[]): number {
  let min = Infinity;
  for (let i = 1; i < ring.length; i++) {
    const [ax, ay] = ring[i - 1];
    const [bx, by] = ring[i];
    const dx = bx - ax;
    const dy = by - ay;
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / lengthSq));
    min = Math.min(min, Math.hypot(x - (ax + t * dx), y - (ay + t * dy)));
  }
  return min;
}

const ENTRANCE_HIT_PX = 6;

function applyEntrances(map: MapLibreMap, data: FeatureCollection): void {
  map.getSource<GeoJSONSource>(ENTRANCE_SOURCE_ID)?.setData(data);
}

/**
 * Подъезды дома в точке [lat, lon] (точка внутри дома) и стрелки к ним.
 * Дом - самый крупный контур здания под точкой, со всеми кусками из
 * соседних тайлов (у кусков общий id). Подъезд - точка слоя entrance не
 * дальше ENTRANCE_MAX_WALL_DISTANCE_M от стены; стрелка - снаружи напротив
 * входа, повёрнута к нему.
 */
function buildingEntrances(map: MapLibreMap, [lat, lon]: [number, number]): FeatureCollection {
  const sourceId = Object.entries(map.getStyle().sources).find(([, source]) => source.type === 'vector')?.[0];
  if (!sourceId) return EMPTY_COLLECTION;

  const point: Position = [lon, lat];
  let best: { id: string | number; area: number } | null = null;
  const buildings = map.querySourceFeatures(sourceId, { sourceLayer: 'building' });
  for (const feature of buildings) {
    if (feature.id == null) continue;
    for (const rings of polygonsOf(feature.geometry)) {
      if (!isPointInPolygon(point, rings)) continue;
      const area = ringArea(rings[0]);
      if (!best || area > best.area) best = { id: feature.id, area };
    }
  }
  if (!best) return EMPTY_COLLECTION;
  const bestId = best.id;
  const pieces = buildings.filter(f => f.id === bestId).flatMap(f => polygonsOf(f.geometry));

  // метры по долготе короче - всё считаем в локальных метрах
  const kx = Math.cos((lat * Math.PI) / 180) * METERS_PER_DEGREE;
  const ky = METERS_PER_DEGREE;
  const toM = ([x, y]: Position): [number, number] => [x * kx, y * ky];
  const toDeg = ([x, y]: [number, number]): Position => [x / kx, y / ky];

  const features: Feature[] = [];
  const seen = new Set<string>();
  for (const entrance of map.querySourceFeatures(sourceId, { sourceLayer: 'entrance' })) {
    if (entrance.geometry.type !== 'Point') continue;
    const position = entrance.geometry.coordinates;
    const key = position.map(v => v.toFixed(7)).join(',');
    if (seen.has(key)) continue; // точка повторяется в буфере соседних тайлов

    // ближайшая стена дома
    const p = toM(position);
    let nearest: { distance: number; a: [number, number]; b: [number, number] } | null = null;
    for (const rings of pieces) {
      for (const ring of rings) {
        for (let i = 1; i < ring.length; i++) {
          const a = toM(ring[i - 1]);
          const b = toM(ring[i]);
          const dx = b[0] - a[0];
          const dy = b[1] - a[1];
          const len2 = dx * dx + dy * dy;
          const t = len2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)) : 0;
          const distance = Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
          if (!nearest || distance < nearest.distance) nearest = { distance, a, b };
        }
      }
    }
    if (!nearest || nearest.distance > ENTRANCE_MAX_WALL_DISTANCE_M) continue;
    seen.add(key);

    const props = entrance.properties ?? {};
    const ref = props.ref != null && props.ref !== '' ? String(props.ref) : undefined;
    features.push({
      type: 'Feature',
      properties: { kind: 'entrance', ...(ref ? { ref } : {}), ...(props.flats ? { flats: String(props.flats) } : {}) },
      geometry: { type: 'Point', coordinates: position },
    });

    // нормаль к стене, наружу: из двух сторон та, что не внутри дома
    const [ax, ay] = nearest.a;
    const [bx, by] = nearest.b;
    const len = Math.hypot(bx - ax, by - ay) || 1;
    let nx = -(by - ay) / len;
    let ny = (bx - ax) / len;
    const probe = toDeg([p[0] + nx * 1.5, p[1] + ny * 1.5]);
    if (pieces.some(rings => isPointInPolygon(probe, rings))) {
      nx = -nx;
      ny = -ny;
    }
    const arrow = toDeg([p[0] + nx * ENTRANCE_ARROW_OFFSET_M, p[1] + ny * ENTRANCE_ARROW_OFFSET_M]);
    // стрелка смотрит на вход: против нормали; азимут - по часовой от севера
    const bearing = (Math.atan2(-nx, -ny) * 180) / Math.PI;
    features.push({
      type: 'Feature',
      properties: { kind: 'arrow', bearing },
      geometry: { type: 'Point', coordinates: arrow },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** Подъезд под курсором (кружок выбранного дома) или null. */
function entranceAt(
  map: MapLibreMap,
  point: Point,
): { lat: number; lon: number; ref: string | null; flats: string | null } | null {
  if (!map.getLayer(ENTRANCE_LAYER_ID)) return null;
  const [feature] = map.queryRenderedFeatures(
    [
      [point.x - ENTRANCE_HIT_PX, point.y - ENTRANCE_HIT_PX],
      [point.x + ENTRANCE_HIT_PX, point.y + ENTRANCE_HIT_PX],
    ],
    { layers: [ENTRANCE_LAYER_ID] },
  );
  if (!feature || feature.geometry.type !== 'Point') return null;
  const [lon, lat] = feature.geometry.coordinates;
  const text = (key: string) => {
    const value = feature.properties?.[key];
    return value == null || value === '' ? null : String(value);
  };
  return { lat, lon, ref: text('ref'), flats: text('flats') };
}

/** Название улицы под курсором или null, если клик не по дороге. Линии дорог
 *  в тайлах без названий - их берём из ближайшей линии transportation_name. */
function streetNameAt(map: MapLibreMap, point: Point): string | null {
  const roadLayers = map
    .getStyle()
    .layers.filter(
      layer =>
        layer.type === 'line' &&
        layer['source-layer'] === 'transportation' &&
        !layer.id.includes('rail'),
    );
  if (!roadLayers.length) return null;

  const roads = map.queryRenderedFeatures(
    [
      [point.x - ROAD_HIT_PX, point.y - ROAD_HIT_PX],
      [point.x + ROAD_HIT_PX, point.y + ROAD_HIT_PX],
    ],
    { layers: roadLayers.map(layer => layer.id) },
  );
  if (!roads.length) return null;

  const sourceId = roads[0].source;
  let best: string | null = null;
  let bestDistance = STREET_NAME_PX;
  for (const feature of map.querySourceFeatures(sourceId, { sourceLayer: 'transportation_name' })) {
    const name = (feature.properties?.['name:ru'] ?? feature.properties?.name) as string | undefined;
    if (!name) continue;
    const lines =
      feature.geometry.type === 'LineString'
        ? [feature.geometry.coordinates]
        : feature.geometry.type === 'MultiLineString'
          ? feature.geometry.coordinates
          : [];
    for (const line of lines) {
      for (let i = 1; i < line.length; i++) {
        const a = map.project(line[i - 1] as [number, number]);
        const b = map.project(line[i] as [number, number]);
        const distance = distanceToSegment(point, a, b);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = name;
        }
      }
    }
  }
  return best;
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function applyHighlight(map: MapLibreMap, geometry: Geometry | null): void {
  // источник появляется только после 'load' - до этого подсветка ждёт в highlightRef
  const source = map.getSource<GeoJSONSource>(HIGHLIGHT_SOURCE_ID);
  source?.setData(
    geometry ? { type: 'Feature', properties: {}, geometry } : EMPTY_COLLECTION,
  );
}

interface RouteLines {
  readonly line: LineString | null;
  readonly tolls: readonly LineString[];
  readonly alternatives: readonly LineString[];
}

function applyRoute(map: MapLibreMap, { line, tolls, alternatives }: RouteLines): void {
  // как и подсветка - источник появляется только после 'load'
  map.getSource<GeoJSONSource>(ROUTE_SOURCE_ID)?.setData(
    line
      ? {
          type: 'FeatureCollection',
          features: [
            { type: 'Feature', properties: { kind: 'main' }, geometry: line },
            ...tolls.map(
              (geometry): Feature => ({ type: 'Feature', properties: { kind: 'toll' }, geometry }),
            ),
            ...alternatives.map(
              (geometry): Feature => ({ type: 'Feature', properties: { kind: 'alternative' }, geometry }),
            ),
          ],
        }
      : EMPTY_COLLECTION,
  );
}

export default MapView;
